import React, { useEffect, useMemo, useState } from "react";
import { Form, Select, Switch, Divider, Alert, Spin } from "antd";
import type { FormInstance } from "antd";
import { GetSkins, GetSceneNames, getJavamonImage } from "../../../dao/jutge/GetJavamonConfig";

const { Option } = Select;

interface Props {
    /** Instancia del Form del botín (compartida con el modal de ConfiguracioJavamons). */
    form: FormInstance;
}

/**
 * Configuración de combate de un botín de tipo "batalla".
 *
 * Replica las MISMAS restricciones de equipamiento por clase de skin que el perfil del
 * jugador (Perfil.tsx):
 *   - Guerrero: arma (espada) + escudo.
 *   - Arquero:  arco + flecha (la flecha requiere arco).
 *   - Mago:     arma (debe ser de tipo "Staves" para poder usar magia) + magia (no 'golpe').
 *   - Luchador: arma + magia de tipo 'golpe'.
 *   - Aventurero / sin clase: solo arma.
 *
 * Expone los campos del formulario: tipo, battle_scene, enemy_id_skin, enemy_id_arma,
 * enemy_id_escut, enemy_id_arc, enemy_id_flecha, enemy_id_magia.
 */
const BotinBattleConfig: React.FC<Props> = ({ form }) => {
    const [loading, setLoading] = useState(false);
    const [skins, setSkins] = useState<any>({});
    const [scenes, setScenes] = useState<any[]>([]);

    const tipo = Form.useWatch("tipo", form);
    const isBatalla = tipo === "batalla";

    const enemySkinId = Form.useWatch("enemy_id_skin", form);
    const enemyArmaId = Form.useWatch("enemy_id_arma", form);
    const enemyArcId = Form.useWatch("enemy_id_arc", form);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const s = await GetSkins();
                if (s) setSkins(s);
                const sc = await GetSceneNames();
                if (Array.isArray(sc)) setScenes(sc);
            } catch (e) {
                console.error("Error cargando catálogos de batalla:", e);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const skinsList: any[] = skins.skins || [];
    const armasList: any[] = skins.armes || [];
    const escutsList: any[] = skins.escuts || [];
    const arcsList: any[] = skins.arcs || [];
    const flechasList: any[] = skins.flechas || [];
    const magiasList: any[] = skins.magias || [];

    const selectedSkin = useMemo(
        () => skinsList.find((s: any) => s.id === enemySkinId),
        [skinsList, enemySkinId]
    );
    const skinType: string = selectedSkin?.type || "";

    const selectedArma = useMemo(
        () => armasList.find((a: any) => a.id === enemyArmaId),
        [armasList, enemyArmaId]
    );
    const isStavesEquipped = !!selectedArma?.ident?.startsWith("Staves");

    // Qué slots se muestran según la clase de la skin (igual que Perfil.tsx).
    const showArma = skinType !== "Arquero" && skinType !== ""; // Guerrero, Mago, Luchador, Aventurero
    const showEscut = skinType === "Guerrero";
    const showArc = skinType === "Arquero";
    const showFlecha = skinType === "Arquero";
    const showMagia = skinType === "Mago" || skinType === "Luchador";

    // Magias permitidas: Luchador solo 'golpe', Mago todo menos 'golpe'.
    const magiasPermitidas = useMemo(() => {
        return magiasList.filter((m: any) => {
            if (m.active !== 1) return false;
            if (skinType === "Luchador" && m.es_escudo !== "golpe") return false;
            if (skinType === "Mago" && m.es_escudo === "golpe") return false;
            return true;
        });
    }, [magiasList, skinType]);

    const activeOnly = (list: any[]) => list.filter((x: any) => x.active === 1);

    const optionWithImg = (item: any, typeSeg: string) => (
        <Option key={item.id} value={item.id} label={item.nom}>
            <div style={{ display: "flex", alignItems: "center" }}>
                <img
                    src={getJavamonImage({ ...item, _type: typeSeg })}
                    alt=""
                    style={{ width: 20, height: 20, marginRight: 8, objectFit: "contain" }}
                    onError={(e) => { (e.target as HTMLImageElement).style.visibility = "hidden"; }}
                />
                {item.nom || `#${item.id}`}
                {item.type ? <span style={{ marginLeft: 6, color: "#999" }}>({item.type})</span> : null}
            </div>
        </Option>
    );

    return (
        <>
            <Divider style={{ margin: "8px 0" }} />
            <Form.Item
                name="tipo"
                label="Tipo de botín"
                tooltip="En un botín de batalla, al interactuar se abre un combate; solo si ganas obtienes las recompensas."
                initialValue="normal"
            >
                <Select>
                    <Option value="normal">Normal (recompensa directa)</Option>
                    <Option value="batalla">Batalla (combate antes de recompensar)</Option>
                </Select>
            </Form.Item>

            {/* Campos ocultos para que el equipamiento del enemigo siempre se envíe (aunque
                un slot no se muestre por la clase, mantenemos su valor controlado por el form). */}
            {!isBatalla && (
                <>
                    <Form.Item name="battle_scene" hidden><input /></Form.Item>
                    <Form.Item name="enemy_id_skin" hidden><input /></Form.Item>
                    <Form.Item name="enemy_id_arma" hidden><input /></Form.Item>
                    <Form.Item name="enemy_id_escut" hidden><input /></Form.Item>
                    <Form.Item name="enemy_id_arc" hidden><input /></Form.Item>
                    <Form.Item name="enemy_id_flecha" hidden><input /></Form.Item>
                    <Form.Item name="enemy_id_magia" hidden><input /></Form.Item>
                </>
            )}

            {isBatalla && (
                <div style={{ background: "#fff7e6", border: "1px solid #ffd591", borderRadius: 8, padding: 12 }}>
                    <Alert
                        type="warning"
                        showIcon
                        style={{ marginBottom: 12 }}
                        message="Configura el combate"
                        description="El jugador deberá derrotar a este enemigo en la escena indicada. Si pierde, el botín se pierde y no podrá volver a reclamarlo."
                    />
                    {loading ? (
                        <div style={{ textAlign: "center", padding: 16 }}><Spin /></div>
                    ) : (
                        <>
                            <Form.Item
                                name="battle_scene"
                                label="Escena del combate (Battleground)"
                                rules={[{ required: true, message: "Elige la escena del combate" }]}
                            >
                                <Select
                                    showSearch
                                    placeholder="Escena Battleground"
                                    optionFilterProp="children"
                                    filterOption={(input, option) =>
                                        String(option?.children ?? "").toLowerCase().includes(input.toLowerCase())
                                    }
                                >
                                    {scenes.map((sc: any) => (
                                        <Option key={sc.id} value={sc.scene_name}>
                                            {sc.scene_name}{sc.display_name ? ` — ${sc.display_name}` : ""}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item
                                name="enemy_id_skin"
                                label="Skin del enemigo (define su clase y stats base)"
                                rules={[{ required: true, message: "Elige la skin del enemigo" }]}
                            >
                                <Select showSearch optionFilterProp="label" placeholder="Skin del enemigo">
                                    {activeOnly(skinsList).map((s: any) => optionWithImg(s, "skin"))}
                                </Select>
                            </Form.Item>

                            {skinType && (
                                <Alert
                                    type="info"
                                    showIcon
                                    style={{ marginBottom: 12 }}
                                    message={`Clase: ${skinType}`}
                                    description={
                                        skinType === "Guerrero" ? "Combate cuerpo a cuerpo. Equipa arma y escudo." :
                                        skinType === "Arquero" ? "Combate a distancia con arco y flechas." :
                                        skinType === "Mago" ? "Lanza magia (requiere un arma de tipo 'Staves')." :
                                        skinType === "Luchador" ? "Combate con magia de tipo golpe." :
                                        "Clase sin equipamiento especializado."
                                    }
                                />
                            )}

                            {showArma && (
                                <Form.Item name="enemy_id_arma" label={skinType === "Mago" ? "Arma (usa 'Staves' para habilitar magia)" : "Arma"}>
                                    <Select allowClear showSearch optionFilterProp="label" placeholder="Arma">
                                        {activeOnly(armasList).map((a: any) => optionWithImg(a, "arma"))}
                                    </Select>
                                </Form.Item>
                            )}

                            {showEscut && (
                                <Form.Item name="enemy_id_escut" label="Escudo">
                                    <Select allowClear showSearch optionFilterProp="label" placeholder="Escudo">
                                        {activeOnly(escutsList).map((e: any) => optionWithImg(e, "escut"))}
                                    </Select>
                                </Form.Item>
                            )}

                            {showArc && (
                                <Form.Item name="enemy_id_arc" label="Arco">
                                    <Select allowClear showSearch optionFilterProp="label" placeholder="Arco">
                                        {activeOnly(arcsList).map((a: any) => optionWithImg(a, "arc"))}
                                    </Select>
                                </Form.Item>
                            )}

                            {showFlecha && (
                                <Form.Item
                                    name="enemy_id_flecha"
                                    label="Flechas"
                                    tooltip={!enemyArcId ? "Elige primero un arco" : undefined}
                                >
                                    <Select allowClear showSearch optionFilterProp="label" placeholder="Flechas" disabled={!enemyArcId}>
                                        {activeOnly(flechasList).map((f: any) => optionWithImg(f, "flecha"))}
                                    </Select>
                                </Form.Item>
                            )}

                            {showMagia && (
                                <Form.Item
                                    name="enemy_id_magia"
                                    label="Magia"
                                    tooltip={skinType === "Mago" && !isStavesEquipped ? "Equipa un arma de tipo 'Staves' para usar magia" : undefined}
                                >
                                    <Select
                                        allowClear
                                        showSearch
                                        optionFilterProp="label"
                                        placeholder="Magia"
                                        disabled={skinType === "Mago" && !isStavesEquipped}
                                    >
                                        {magiasPermitidas.map((m: any) => optionWithImg(m, "magia"))}
                                    </Select>
                                </Form.Item>
                            )}
                        </>
                    )}
                </div>
            )}
        </>
    );
};

export default BotinBattleConfig;
